import { and, asc, eq, gt } from "drizzle-orm";
import type { DbClient } from "@/server/platform/db/client";
import { resolvePoolVariantId } from "@/modules/products/domain/shared-stock";
import { goodsReceipts, stockLots } from "@/server/platform/db/schema/inventory";
import type { DbTransaction } from "@/shared/types";
import type { StockLotDetailDTO } from "../types";

type DbLike = DbTransaction | DbClient;

export type AvailableStockLot = {
  id: string;
  quantityRemaining: number;
  unitLandedCost: string;
};

/** Lot ທັງໝົດຂອງ variant — ຄ່າເລີ່ມ lot ເກົ່າກ່ອນ (FIFO) */
export async function listStockLotsForVariant(
  client: DbLike,
  variantId: string,
  options?: { includeDepleted?: boolean },
): Promise<StockLotDetailDTO[]> {
  const poolVariantId = await resolvePoolVariantId(client, variantId);
  const conditions = [eq(stockLots.variantId, poolVariantId)];
  if (!options?.includeDepleted) {
    conditions.push(gt(stockLots.quantityRemaining, 0));
  }

  return client
    .select({
      id: stockLots.id,
      receiptId: stockLots.receiptId,
      receiptLineId: stockLots.receiptLineId,
      receiptNumber: goodsReceipts.receiptNumber,
      bundleAssemblyId: stockLots.bundleAssemblyId,
      variantId: stockLots.variantId,
      quantityReceived: stockLots.quantityReceived,
      quantityRemaining: stockLots.quantityRemaining,
      unitLandedCost: stockLots.unitLandedCost,
      receivedAt: stockLots.receivedAt,
    })
    .from(stockLots)
    .leftJoin(goodsReceipts, eq(stockLots.receiptId, goodsReceipts.id))
    .where(and(...conditions))
    .orderBy(asc(stockLots.receivedAt), asc(stockLots.id));
}

/** Lot ຂອງໃບຮັບເຂົ້າ */
export async function listStockLotsForReceipt(
  client: DbLike,
  receiptId: string,
): Promise<StockLotDetailDTO[]> {
  return client
    .select({
      id: stockLots.id,
      receiptId: stockLots.receiptId,
      receiptLineId: stockLots.receiptLineId,
      receiptNumber: goodsReceipts.receiptNumber,
      variantId: stockLots.variantId,
      quantityReceived: stockLots.quantityReceived,
      quantityRemaining: stockLots.quantityRemaining,
      unitLandedCost: stockLots.unitLandedCost,
      receivedAt: stockLots.receivedAt,
    })
    .from(stockLots)
    .innerJoin(goodsReceipts, eq(stockLots.receiptId, goodsReceipts.id))
    .where(eq(stockLots.receiptId, receiptId))
    .orderBy(asc(stockLots.receivedAt), asc(stockLots.id));
}

/** Lot ที่ยังเหลือ — เรียงเก่าก่อน (FIFO) */
export async function listAvailableLotsForVariant(
  client: DbLike,
  variantId: string,
): Promise<AvailableStockLot[]> {
  const poolVariantId = await resolvePoolVariantId(client, variantId);
  return client
    .select({
      id: stockLots.id,
      quantityRemaining: stockLots.quantityRemaining,
      unitLandedCost: stockLots.unitLandedCost,
    })
    .from(stockLots)
    .where(
      and(
        eq(stockLots.variantId, poolVariantId),
        gt(stockLots.quantityRemaining, 0),
      ),
    )
    .orderBy(asc(stockLots.receivedAt), asc(stockLots.id));
}

export async function decrementLotRemaining(
  client: DbTransaction,
  lotId: string,
  quantity: number,
) {
  const rows = await client
    .select({ quantityRemaining: stockLots.quantityRemaining })
    .from(stockLots)
    .where(eq(stockLots.id, lotId))
    .limit(1);
  const lot = rows[0];
  if (!lot) throw new Error("Stock lot not found");
  if (lot.quantityRemaining < quantity) {
    throw new Error("Insufficient lot quantity");
  }

  await client
    .update(stockLots)
    .set({ quantityRemaining: lot.quantityRemaining - quantity })
    .where(eq(stockLots.id, lotId));
}

export async function incrementLotRemaining(
  client: DbTransaction,
  lotId: string,
  quantity: number,
) {
  const rows = await client
    .select({ quantityRemaining: stockLots.quantityRemaining })
    .from(stockLots)
    .where(eq(stockLots.id, lotId))
    .limit(1);
  const lot = rows[0];
  if (!lot) throw new Error("Stock lot not found");

  await client
    .update(stockLots)
    .set({ quantityRemaining: lot.quantityRemaining + quantity })
    .where(eq(stockLots.id, lotId));
}
